import { getTurnstileSiteKey, loadTurnstileScript } from './turnstile'

type PendingChallenge = {
  resolve: (token: string) => void
  reject: (error: Error) => void
  timeoutId: ReturnType<typeof setTimeout>
}

const TURNSTILE_CONTAINER_ID = 'turnstile-preclearance-container'
const TURNSTILE_TIMEOUT_MS = 60000
const CLOUDFLARE_CHALLENGE_HEADER = 'cf-mitigated'
const CLOUDFLARE_CHALLENGE_VALUE = 'challenge'

let widgetId: string | null = null
let pendingChallenge: PendingChallenge | null = null
let preclearanceInFlight: Promise<void> | null = null

function rejectPendingChallenge(message: string) {
  if (!pendingChallenge) return

  clearTimeout(pendingChallenge.timeoutId)
  const pending = pendingChallenge
  pendingChallenge = null
  pending.reject(new Error(message))
}

function getTurnstileContainer(): HTMLDivElement {
  const existingContainer = document.getElementById(TURNSTILE_CONTAINER_ID)
  if (existingContainer instanceof HTMLDivElement) return existingContainer

  const container = document.createElement('div')
  container.id = TURNSTILE_CONTAINER_ID
  container.style.position = 'fixed'
  container.style.right = '24px'
  container.style.bottom = '24px'
  container.style.zIndex = '2147483647'
  document.body.appendChild(container)
  return container
}

async function getWidgetId(): Promise<string> {
  const siteKey = getTurnstileSiteKey()
  await loadTurnstileScript()

  if (!window.turnstile) {
    throw new Error('Turnstile is not available')
  }

  if (widgetId) return widgetId

  const container = getTurnstileContainer()
  widgetId = window.turnstile.render(`#${container.id}`, {
    sitekey: siteKey,
    execution: 'execute',
    appearance: 'interaction-only',
    callback: token => {
      if (!pendingChallenge) return

      clearTimeout(pendingChallenge.timeoutId)
      const pending = pendingChallenge
      pendingChallenge = null
      pending.resolve(token)
    },
    'error-callback': errorCode => {
      rejectPendingChallenge(`Turnstile failed: ${errorCode}`)
    },
    'expired-callback': () => {
      rejectPendingChallenge('Turnstile token expired')
    },
    'timeout-callback': () => {
      rejectPendingChallenge('Turnstile challenge timed out')
    },
  })

  return widgetId
}

async function executePreclearanceTurnstile(): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('Turnstile is not available on the server')
  }

  const currentWidgetId = await getWidgetId()

  if (!window.turnstile) {
    throw new Error('Turnstile is not available')
  }

  rejectPendingChallenge('Turnstile challenge was replaced')

  return new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingChallenge = null
      reject(new Error('Turnstile challenge timed out'))
    }, TURNSTILE_TIMEOUT_MS)

    pendingChallenge = { resolve, reject, timeoutId }

    try {
      window.turnstile?.reset(currentWidgetId)
      window.turnstile?.execute(currentWidgetId)
    } catch (error) {
      clearTimeout(timeoutId)
      pendingChallenge = null
      reject(error instanceof Error ? error : new Error('Turnstile failed'))
    }
  })
}

export function isCloudflareChallengeResponse(response: Response): boolean {
  return (
    response.headers.get(CLOUDFLARE_CHALLENGE_HEADER)?.toLowerCase() ===
    CLOUDFLARE_CHALLENGE_VALUE
  )
}

async function verifyPreclearanceToken(token: string): Promise<void> {
  const response = await fetch('/api/verifycaptcha', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })

  if (isCloudflareChallengeResponse(response)) {
    throw new Error('Cloudflare challenged Turnstile token verification')
  }

  if (!response.ok) {
    throw new Error('Turnstile pre-clearance token verification failed')
  }
}

async function performCloudflareChallengeClearance(): Promise<void> {
  const token = await executePreclearanceTurnstile()
  await verifyPreclearanceToken(token)
}

export async function clearCloudflareChallengeWithTurnstile(): Promise<void> {
  if (!preclearanceInFlight) {
    preclearanceInFlight = performCloudflareChallengeClearance().finally(() => {
      preclearanceInFlight = null
    })
  }

  return preclearanceInFlight
}

function getRetryInput(input: RequestInfo | URL): RequestInfo | URL {
  if (typeof Request === 'undefined' || !(input instanceof Request)) {
    return input
  }

  return input.clone()
}

function getRetryInit(init: RequestInit | undefined): RequestInit | undefined {
  if (!init?.signal) return init

  const { signal: _signal, ...retryInit } = init
  return retryInit
}

export async function fetchWithCloudflarePreclearance(
  input: RequestInfo | URL,
  init?: RequestInit,
  // An optional deadline for the entire request, including challenge recovery.
  // Existing auth callers only time out the initial fetch via init.signal.
  requestSignal?: AbortSignal
): Promise<Response> {
  requestSignal?.throwIfAborted()
  const retryInput = getRetryInput(input)
  const response = await fetch(
    input,
    requestSignal ? { ...init, signal: requestSignal } : init
  )

  if (
    typeof window === 'undefined' ||
    !isCloudflareChallengeResponse(response)
  ) {
    return response
  }

  await clearCloudflareChallengeWithTurnstile()
  requestSignal?.throwIfAborted()
  const retryInit = getRetryInit(init)
  return fetch(
    retryInput,
    requestSignal ? { ...retryInit, signal: requestSignal } : retryInit
  )
}
